import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

// Fallback images
import gallery1 from "@/assets/gallery-1.jpg";
import gallery2 from "@/assets/gallery-2.jpg";
import gallery3 from "@/assets/gallery-3.jpg";
import gallery4 from "@/assets/gallery-4.jpg";
import gallery5 from "@/assets/gallery-5.jpg";
import gallery6 from "@/assets/gallery-6.jpg";

interface GalleryItem {
  image: string;
  couple: string;
  venue: string;
  location: string;
}

const fallbackGalleries: GalleryItem[] = [
  { image: gallery1, couple: "Jessica & Michael", venue: "The Ashford Estate", location: "NJ" },
  { image: gallery2, couple: "Sarah & David", venue: "Park Chateau", location: "NJ" },
  { image: gallery3, couple: "Emily & James", venue: "Nanina's in the Park", location: "NJ" },
  { image: gallery4, couple: "Amanda & Chris", venue: "The Manor", location: "NJ" },
  { image: gallery5, couple: "Rachel & Andrew", venue: "St. Patrick's Cathedral", location: "NYC" },
  { image: gallery6, couple: "Lauren & Matt", venue: "Crystal Plaza", location: "NJ" },
];

const Portfolio = () => {
  const [galleries, setGalleries] = useState<GalleryItem[]>(fallbackGalleries);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolioMedia = async () => {
      try {
        // Fetch media assigned to the portfolio page location
        const { data: locationData, error: locationError } = await supabase
          .from("portfolio_media_locations")
          .select(`
            media_id,
            portfolio_media (
              id,
              title,
              url,
              alt_text,
              is_active,
              sort_order
            )
          `)
          .eq("location", "portfolio_page");

        if (locationError) throw locationError;

        if (locationData && locationData.length > 0) {
          // Filter active media and sort by sort_order
          const activeMedia = locationData
            .filter((item: any) => item.portfolio_media?.is_active)
            .sort((a: any, b: any) => 
              (a.portfolio_media?.sort_order || 0) - (b.portfolio_media?.sort_order || 0)
            );

          if (activeMedia.length > 0) {
            const dbGalleries: GalleryItem[] = activeMedia.map((item: any, index: number) => {
              const media = item.portfolio_media;
              // Parse title to extract couple name and venue if formatted as "Couple Name - Venue, Location"
              const title = media.title || "";
              const altText = media.alt_text || "";
              
              // Try to parse structured data from title or alt text
              let couple = `Couple ${index + 1}`;
              let venue = "Beautiful Venue";
              let location = "NJ";

              // Check if alt_text has venue info
              if (altText.includes(" at ")) {
                const parts = altText.split(" at ");
                couple = parts[0].replace(" wedding", "").trim();
                venue = parts[1].trim();
              } else if (title) {
                couple = title;
              }

              return {
                image: media.url,
                couple,
                venue,
                location,
              };
            });

            setGalleries(dbGalleries);
          }
        }
      } catch (error) {
        console.error("Error fetching portfolio media:", error);
        // Keep fallback galleries on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPortfolioMedia();
  }, []);

  return (
    <>
      <Helmet>
        <title>Wedding Photography Portfolio | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Browse our wedding photography portfolio featuring real couples from NJ, FL & CO. See our style and imagine your love story."
        />
        <meta property="og:title" content="Wedding Photography Portfolio | Pixie Clicks" />
        <meta property="og:description" content="Browse our wedding photography portfolio featuring real couples. See our style and imagine your love story." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/portfolio" />
        <link rel="canonical" href="https://pixieclicks.com/portfolio" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            "name": "Pixie Clicks Wedding Photography Portfolio",
            "description": "Wedding photography portfolio featuring real couples from New Jersey, Florida, and Colorado.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="py-20 md:py-28 bg-muted/30">
          <div className="container-wide text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-serif text-4xl font-semibold text-foreground md:text-5xl"
            >
              Our <span className="text-primary">Portfolio</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              A glimpse into the love stories we've had the honor of capturing. Every wedding is unique, and we approach each one with fresh eyes and open hearts.
            </motion.p>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="section-padding">
          <div className="container-wide">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {galleries.map((gallery, index) => (
                <motion.div
                  key={`${gallery.couple}-${index}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative overflow-hidden rounded-xl aspect-[4/5]"
                >
                  <img
                    src={gallery.image}
                    alt={`${gallery.couple} wedding at ${gallery.venue}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading={index < 3 ? "eager" : "lazy"}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-primary-foreground opacity-0 transition-all duration-300 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0">
                    <h3 className="font-serif text-xl font-semibold">{gallery.couple}</h3>
                    <p className="text-sm text-primary-foreground/80">{gallery.venue}, {gallery.location}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow text-center">
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Ready to Add Your Story?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Let's create something beautiful together. Check your date and let's start planning.
            </p>
            <Button variant="hero" size="xl" className="mt-8" asChild>
              <Link to="/contact">Check Availability</Link>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Portfolio;
